import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Lightbulb, 
  Building2, 
  TrendingUp, 
  TrendingDown, 
  Clock,
  DollarSign,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Info
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { getRecommendations, formatCurrency, formatPercent } from "@/lib/api";

const ActionBadge = ({ type, priority }) => {
  const config = {
    BUY: { color: "bg-emerald-100 text-emerald-700", icon: TrendingUp },
    REFINANCE: { color: "bg-blue-100 text-blue-700", icon: DollarSign },
    CONSIDER_SELL: { color: "bg-amber-100 text-amber-700", icon: TrendingDown },
    HOLD: { color: "bg-slate-100 text-slate-700", icon: CheckCircle },
    WAIT: { color: "bg-purple-100 text-purple-700", icon: Clock },
  };
  
  const { color, icon: Icon } = config[type] || config.HOLD;
  
  return (
    <Badge className={`${color} flex items-center gap-1 font-medium`}>
      <Icon className="w-3 h-3" />
      {type.replace(/_/g, " ")}
    </Badge>
  );
};

const RecommendationCard = ({ recommendation }) => {
  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow" data-testid={`recommendation-${recommendation.property_id}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-slate-600" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-slate-900 line-clamp-1">
                {recommendation.address}
              </h3>
              <p className="text-sm text-slate-500">
                {formatCurrency(recommendation.current_value)}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-lg font-bold ${recommendation.capital_growth >= 0 ? "text-emerald-600" : "text-red-500"}`}>
              {recommendation.capital_growth >= 0 ? "+" : ""}{formatPercent(recommendation.capital_growth)}
            </p>
            <p className="text-xs text-slate-500">Capital Growth</p>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-3 gap-4 py-4 border-y border-slate-100">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide">Equity</p>
            <p className="font-semibold text-emerald-600">{formatCurrency(recommendation.equity)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide">LVR</p>
            <p className="font-semibold text-slate-900">{formatPercent(recommendation.lvr)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide">Yield</p>
            <p className="font-semibold text-slate-900">{formatPercent(recommendation.rental_yield)}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3 mt-4">
          {recommendation.actions.length > 0 ? (
            recommendation.actions.map((action, index) => (
              <div 
                key={index}
                className={`p-3 rounded-lg ${
                  action.priority === "high" ? "bg-emerald-50" :
                  action.priority === "medium" ? "bg-blue-50" :
                  action.priority === "low" ? "bg-amber-50" :
                  "bg-slate-50"
                }`}
              >
                <div className="flex items-start gap-2">
                  <ActionBadge type={action.type} priority={action.priority} />
                </div>
                <p className="text-sm text-slate-600 mt-2">{action.reason}</p>
              </div>
            ))
          ) : (
            <div className="p-3 bg-slate-50 rounded-lg text-center">
              <p className="text-sm text-slate-500">No specific actions recommended</p>
            </div>
          )}
        </div>

        {/* View Property Link */}
        <Link to={`/properties/${recommendation.property_id}`}>
          <Button variant="ghost" className="w-full mt-4 text-[#547d91]">
            View Property Details
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
};

export default function Recommendations() {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        const response = await getRecommendations();
        setData(response.data);
      } catch (error) {
        console.error("Error fetching recommendations:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchRecommendations();
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-32" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  if (!data || data.recommendations.length === 0) {
    return (
      <div className="text-center py-12" data-testid="recommendations-empty">
        <Lightbulb className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h3 className="font-heading text-xl font-semibold text-slate-700 mb-2">
          No Properties Yet
        </h3>
        <p className="text-slate-500 mb-6">
          Add properties to get buy/sell recommendations
        </p>
        <Link to="/properties/new">
          <Button className="bg-[#547d91] hover:bg-[#456a7a] rounded-full">
            Add Property
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="recommendations-page">
      {/* Header */}
      <div>
        <h2 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">
          Buy/Sell Recommendations
        </h2>
        <p className="text-slate-500 mt-1">
          Smart timing insights for your portfolio decisions
        </p>
      </div>

      {/* Portfolio Summary */}
      <Card className="bg-gradient-to-br from-[#547d91] to-[#456a7a] text-white">
        <CardContent className="p-6 lg:p-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="space-y-2">
              <p className="text-white/70 text-sm uppercase tracking-wide">Portfolio Analysis</p>
              <p className="text-lg lg:text-xl max-w-2xl">{data.summary}</p>
            </div>
            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-3xl font-bold">{formatCurrency(data.total_equity)}</p>
                <p className="text-white/70 text-sm">Total Equity</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold">{formatPercent(data.average_lvr)}</p>
                <p className="text-white/70 text-sm">Avg LVR</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {(() => {
          const buySignals = data.recommendations.filter(r => 
            r.actions.some(a => a.type === "BUY")
          ).length;
          const refinanceSignals = data.recommendations.filter(r => 
            r.actions.some(a => a.type === "REFINANCE")
          ).length;
          const waitSignals = data.recommendations.filter(r => 
            r.actions.some(a => a.type === "WAIT")
          ).length;
          const sellSignals = data.recommendations.filter(r => 
            r.actions.some(a => a.type === "CONSIDER_SELL")
          ).length;

          return (
            <>
              <Card className="metric-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-600">{buySignals}</p>
                      <p className="text-xs text-slate-500">Buy Signals</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="metric-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-600">{refinanceSignals}</p>
                      <p className="text-xs text-slate-500">Refinance</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="metric-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-purple-600">{waitSignals}</p>
                      <p className="text-xs text-slate-500">Wait for CGT</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="metric-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-600">{sellSignals}</p>
                      <p className="text-xs text-slate-500">Review Needed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          );
        })()}
      </div>

      {/* Recommendations Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {data.recommendations.map((recommendation) => (
          <RecommendationCard key={recommendation.property_id} recommendation={recommendation} />
        ))}
      </div>

      {/* Info Box */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-slate-500 mt-0.5" />
            <div>
              <h4 className="font-semibold text-slate-700 mb-2">How Recommendations Work</h4>
              <div className="space-y-2 text-sm text-slate-600">
                <p>
                  <strong>BUY Signal:</strong> Triggered when LVR is below 70% and equity exceeds $100,000 - 
                  indicating capacity to leverage for additional property purchases.
                </p>
                <p>
                  <strong>REFINANCE Signal:</strong> When usable equity exceeds $50,000 after maintaining 
                  20% buffer - opportunity to access equity for investment.
                </p>
                <p>
                  <strong>WAIT Signal:</strong> If property has capital gains but hasn't been held for 
                  12 months - waiting qualifies for 50% CGT discount.
                </p>
                <p>
                  <strong>CONSIDER SELL:</strong> Annualised capital growth below 5% PA combined with 
                  rental yield under 3% suggests capital may perform better elsewhere.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
